import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { WagoApiStatusCode } from '@shared/service/wago-api-status-code';
import { BehaviorSubject, lastValueFrom, NEVER, Observable, Subject, Subscriber } from 'rxjs';
import { Log } from '@shared/debug/log';
import { NumberUtils } from '../util/number-utils';
import { OverlayBackground, OverlayType } from '../overlay/overlay.type';
import { StringUtils } from '../util/string-utils';

interface RequestQueueItem {
  request: Function;
  overlayType: OverlayType;
  disableBasicErrorHandling: Function;
  disableToElevateUserFromGuestByLoggingIn: Function;
  disableActiveCheckAndSessionValidation: Function;
  isFailedRequest: boolean;
  errorHandlingFunction: Function | null;
}

export interface Parameters {
  isErrorHandlingRequired?: boolean;
  bypassActiveCheckAndSessionValidation?: boolean;
  askToElevateUserFromGuestByLoggingIn?: boolean;
  overlayType?: OverlayType | null;
}

interface HttpOptions {
  headers?: HttpHeaders | { [header: string]: string | string[] };
  observe?: 'body';
}

export type RequestProgressInfo = { waiting: boolean, overlayType: OverlayBackground | null }

@Injectable({ providedIn: 'root' })
export class WagoApiClient {
  // The service will be deactivated in case a request failed because a user did not have the rights for such request (maybe session timed out).
  // While the service is not active, all requests without 'bypassActiveCheckAndSessionValidation' flag set to true, will not be executed directly and insteadt put into a queue.
  // Queued requests will be executed once the service is activated again (for example user re-logged-in).
  private _isActive: boolean = true;
  private requestQueue: RequestQueueItem[] = [];

  private _askUserToForwardWithLogin = new Subject<boolean>();

  private readonly _apiUrl= '/wago/api/';

  private _httpErrorsSubject = new Subject<any>();
  private _initialRequestFailedSubject = new BehaviorSubject<boolean | undefined>(undefined);

  private _requestProgress = new BehaviorSubject<RequestProgressInfo>({
    waiting: false,
    overlayType: OverlayBackground.color,
  });

  constructor(
    private httpClient: HttpClient,
  ) {
  }

  public get $httpErrors(): Observable<any> {
    return this._httpErrorsSubject.asObservable();
  }

  public get $initialRequestFailed(): Observable<boolean | undefined> {
    return this._initialRequestFailedSubject.asObservable();
  }

  public get $requestProgress(): Observable<RequestProgressInfo> {
    return this._requestProgress.asObservable();
  }

  public get askUserToForwardWithLogin(): Observable<boolean> {
    return this._askUserToForwardWithLogin;
  }

  public static getErrorStatusCode(error: any): number {
    if (!error || !error.hasOwnProperty('status')) {
      return NaN;
    }
    const statusCode: number = error['status'];
    return statusCode;
  }

  public static isServiceUnavailableException(error: any): boolean {
    const message = error?.error?.message;
    if (!message) {
      return false;
    }

    return StringUtils.startsWith(message, '503');
  }

  public static isAccessDeniedError(error: any): boolean {
    const statusCode: number = WagoApiClient.getErrorStatusCode(error);
    return statusCode === WagoApiStatusCode.Forbidden || statusCode === WagoApiStatusCode.MethodNotAllowed;
  }

  private createRequestOptions(requestOptions: any): HttpOptions {
    const resultRequestOptions = requestOptions || {
      headers: new HttpHeaders(),
      observe: 'body',
    };
    let headers = resultRequestOptions.headers || new HttpHeaders();
    if (!(headers instanceof HttpHeaders)) {
      headers = new HttpHeaders(headers);
    }
    headers = headers.append('Cache-Control', 'no-cache, no-store, max-age=0, must-revalidate');
    headers = headers.append('Pragma', 'no-cache');
    resultRequestOptions.headers = headers;
    return resultRequestOptions;
  }

  public get isActive(): boolean {
    return this._isActive;
  }

  private async doBasicErrorHandling(error: any): Promise<void> {
    Log.error('Error upon http request: ', error);
    const statusCode: number = WagoApiClient.getErrorStatusCode(error);
    if (!NumberUtils.isNumber(statusCode) || this._initialRequestFailedSubject.value) {
      return;
    }

    if(this.checkInitialRequestFailed()) {
      return;
    }

    this._httpErrorsSubject.next(error);

    error.faultMessageAlreadyDisplayed = true;
  }

  /** If this request failed and was the very first request to the backend, shows an info dialog and sets
   * initialRequestFailed to true so no other requests will be attempted until the page is reloaded */
  private checkInitialRequestFailed(): boolean {
    if (this._initialRequestFailedSubject.value === undefined) {
      this._initialRequestFailedSubject.next(true);
      return true;
    }
    return false;
  }

  public get<T>(url: string, options: any = null, parameters: Parameters = {}): Observable<T> {
    const request: Observable<T> = this.httpClient.get<T>(this._apiUrl + url, this.createRequestOptions(options));
    return this.executeRequest<T>(request, this.getParameters(parameters));
  }

  public getSingle<T>(url: string, options: any = null, parameters: Parameters = {}): Promise<T> {
    return lastValueFrom(this.get<T>(url, options, parameters));
  }

  public post<T>(url: string, data: any, options: any = null, parameters: Parameters = {}): Observable<T> {
    const request: Observable<T> = this.httpClient.post<T>(this._apiUrl + url, data, this.createRequestOptions(options));
    return this.executeRequest(request, this.getParameters(parameters));
  }

  public put<T>(url: string, data: any, options: any = null, parameters: Parameters = {}): Observable<T> {
    const request: Observable<T> = this.httpClient.put<T>(this._apiUrl + url, data, this.createRequestOptions(options));
    return this.executeRequest(request, this.getParameters(parameters));
  }

  public delete<T>(url: string, options: any = null, parameters: Parameters = {}): Observable<T> {
    const request: Observable<T> = this.httpClient.delete<T>(this._apiUrl + url, this.createRequestOptions(options));
    return this.executeRequest(request, this.getParameters(parameters));
  }

  public deleteSingle<T>(url: string, options: any = null, parameters: Parameters = {}): Promise<T> {
    return lastValueFrom(this.delete<T>(url, options, parameters));
  }

  private executeRequest<T>(request: Observable<T>, parameters: Required<Parameters>): Observable<T> {
    if(this._initialRequestFailedSubject.value) {
      return NEVER;
    }
    return new Observable<T>((subscriber: Subscriber<T>) => {
      const requestAction = () => {
        request.subscribe({
          next: (response: T) => this.onSuccess<T>(response, subscriber, parameters.overlayType),
          error: (error: any) => this.onError<T>(error, subscriber, parameters, requestAction)
        });
      };
      if (this.isActive || parameters.bypassActiveCheckAndSessionValidation) {
        this._requestProgress.next({waiting: true, overlayType: parameters.overlayType});
        requestAction();
      } else {
        this.requestQueue.push(this.createRequestQueueItem(requestAction, parameters, false, null));
      }
    });
  }

  private createRequestQueueItem(
    requestAction: Function,
    parameters: Required<Parameters>,
    isFailedRequest: boolean,
    errorHandlingFunction: Function | null
  ): RequestQueueItem {
    const disableBasicErrorHandling: Function = () => (parameters.isErrorHandlingRequired = false);
    const disableToElevateUserFromGuestByLoggingIn: Function = () => (parameters.askToElevateUserFromGuestByLoggingIn = false);
    // in case a request asks to elevate a guest user, we always need to validate session for such request, so that such elevating can take place:
    const disableActiveCheckAndSessionValidation: Function = () => {
      if (!parameters.askToElevateUserFromGuestByLoggingIn) {
        parameters.bypassActiveCheckAndSessionValidation = true;
      }
    };
    return {
      request: requestAction,
      overlayType: parameters.overlayType,
      disableBasicErrorHandling: disableBasicErrorHandling,
      disableToElevateUserFromGuestByLoggingIn: disableToElevateUserFromGuestByLoggingIn,
      disableActiveCheckAndSessionValidation: disableActiveCheckAndSessionValidation,
      isFailedRequest: isFailedRequest,
      errorHandlingFunction: errorHandlingFunction,
    };
  }

  private onSuccess<T>(response: T, subscriber: Subscriber<T>, overlay: OverlayType): void {
    if(this._initialRequestFailedSubject.value === undefined) {
      this._initialRequestFailedSubject.next(false);
    }
    this._requestProgress.next({waiting: false, overlayType: overlay});
    subscriber.next(response);
    subscriber.complete();
  }

  private async onError<T>(error: any, subscriber: Subscriber<T>, parameters: Required<Parameters>, requestAction: Function): Promise<void> {
    this._requestProgress.next({waiting: false, overlayType: parameters.overlayType});
    const statusCode: number = WagoApiClient.getErrorStatusCode(error);
    const errorHandlingFunction: Function = () => this.errorHandlingFunction(subscriber, error, parameters.isErrorHandlingRequired);
    if (statusCode === WagoApiStatusCode.Forbidden || statusCode === WagoApiStatusCode.MethodNotAllowed) {
      // in case request failed because of insufficient rights, we check if user session exists and is still valid (maybe session timed out)
      if (parameters.bypassActiveCheckAndSessionValidation) {
        // Requests executed with bypassActiveCheckAndSessionValidation enabled are executed and handled directly and are not managed by request queue. Thus we trigger error handling immediately:
        await this.errorHandlingFunction(subscriber, error, parameters.isErrorHandlingRequired);
      } else if (this._isActive) {
        // We need to check current user session only in case service is active, otherwise requests are queued, because validation of current user session is already in progress.
        this._isActive = false;
        this.requestQueue.push(this.createRequestQueueItem(requestAction, parameters, true, errorHandlingFunction));
        // check if current user session is valid and optionally elevate guest user to some logged-in user with more rights
        this._askUserToForwardWithLogin.next(parameters.askToElevateUserFromGuestByLoggingIn);
      } else {
        this.requestQueue.push(this.createRequestQueueItem(requestAction, parameters, true, errorHandlingFunction));
      }
    } else {
      if (WagoApiClient.isServiceUnavailableException(error)) {
        error.status = WagoApiStatusCode.ServiceUnavailable;
      }
      // request failed because of some error unrelated to invalid session or user rights, thus we trigger error handling immediately:
      await this.errorHandlingFunction(subscriber, error, parameters.isErrorHandlingRequired);
    }
  }

  private async errorHandlingFunction<T>(subscriber: Subscriber<T>, error: any, isBasicErrorHandlingRequired: boolean): Promise<void> {
    if (isBasicErrorHandlingRequired) {
      await this.doBasicErrorHandling(error);
    }
    subscriber.error(error);
  }

  public activateAndExecuteQueuedRequests(
    retryFailedRequests: boolean,
    triggerErrorHandlingForFailedRequests: boolean,
    disableBasicErrorHandlingForQueuedRequests: boolean,
    disableActiveCheckAndSessionValidationForQueuedRequests: boolean,
    disableToElevateUserFromGuestByLoggingIn: boolean
  ): void {
    const requestItems: RequestQueueItem[] = this.requestQueue;
    this._isActive = true;
    this.requestQueue = [];
    for (const requestItem of requestItems) {
      if (triggerErrorHandlingForFailedRequests && requestItem.isFailedRequest && requestItem.errorHandlingFunction) {
        // error handling for failed requests should include basic error handling insinde error handling method (if request enabled it)
        // disabling of basic error handling applies only to queued requests (meaning requests which did not already fail)
        requestItem.errorHandlingFunction();
      }
      if (disableBasicErrorHandlingForQueuedRequests && requestItem.disableBasicErrorHandling) {
        requestItem.disableBasicErrorHandling();
      }
      if (disableToElevateUserFromGuestByLoggingIn && requestItem.disableToElevateUserFromGuestByLoggingIn) {
        requestItem.disableToElevateUserFromGuestByLoggingIn();
      }
      if (disableActiveCheckAndSessionValidationForQueuedRequests && requestItem.disableActiveCheckAndSessionValidation) {
        requestItem.disableActiveCheckAndSessionValidation();
      }
      if (retryFailedRequests || !requestItem.isFailedRequest) {
        this._requestProgress.next({waiting: true, overlayType: requestItem.overlayType});
        requestItem.request();
      }
    }
  }

  public getParameters(parameters: Parameters): Required<Parameters> {
    const isErrorHandlingRequired: boolean = parameters.isErrorHandlingRequired ?? true;
    const askToElevateUserFromGuestByLoggingIn: boolean = parameters.askToElevateUserFromGuestByLoggingIn ?? false;
    const bypassActiveCheckAndSessionValidation: boolean = askToElevateUserFromGuestByLoggingIn || (parameters.bypassActiveCheckAndSessionValidation ?? false);
    const overlayType: OverlayType = parameters.overlayType === null ? null : (parameters.overlayType ?? OverlayBackground.color);

    // Note: In case a request asks to elevate a guest user, we always need to validate session for such request, so that such elevating can take place:
    return {
      isErrorHandlingRequired,
      bypassActiveCheckAndSessionValidation,
      askToElevateUserFromGuestByLoggingIn,
      overlayType,
    };
  }

  public submitForm(url: string, mapForm: HTMLFormElement): void {
    mapForm.action = this._apiUrl + url;
    mapForm.submit();
  }
}
